import type { IssueStatus, ItemStatus, MagazineStatus } from "./types";

const dateTime = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
const dateOnly = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
const relative = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
const number = new Intl.NumberFormat("en-GB");

export const fmtDateTime = (iso?: string | null) => (iso ? dateTime.format(new Date(iso)) : "—");
export const fmtDate = (iso?: string | null) => (iso ? dateOnly.format(new Date(iso)) : "—");
export const fmtNumber = (n?: number | null) => (n === null || n === undefined ? "—" : number.format(n));

export function fmtAgo(iso?: string | null) {
  if (!iso) return "—";
  const seconds = (new Date(iso).getTime() - Date.now()) / 1000;
  const steps: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 31536000],
    ["month", 2592000],
    ["week", 604800],
    ["day", 86400],
    ["hour", 3600],
    ["minute", 60],
  ];
  for (const [unit, size] of steps) {
    if (Math.abs(seconds) >= size) return relative.format(Math.round(seconds / size), unit);
  }
  return "just now";
}

/** For <input type="datetime-local">: local time without seconds. */
export function toLocalInput(iso?: string | null) {
  const d = iso ? new Date(iso) : new Date(Date.now() + 3600_000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

type Tone = "ok" | "warn" | "bad" | "info" | "violet" | "ember" | "";

export const ITEM_STATUS: Record<ItemStatus, { label: string; tone: Tone }> = {
  found: { label: "Found", tone: "" },
  scraped: { label: "Scraped", tone: "info" },
  checked: { label: "Checked", tone: "violet" },
  waiting_review: { label: "Waiting for review", tone: "warn" },
  approved: { label: "Approved", tone: "ember" },
  published: { label: "Published", tone: "ok" },
  duplicate: { label: "Duplicate", tone: "" },
  rejected: { label: "Rejected", tone: "bad" },
  failed: { label: "Failed", tone: "bad" },
};

export const ISSUE_STATUS: Record<IssueStatus, { label: string; tone: Tone }> = {
  draft: { label: "Draft", tone: "" },
  scheduled: { label: "Scheduled", tone: "info" },
  sending: { label: "Sending", tone: "ember" },
  sent: { label: "Sent", tone: "ok" },
  cancelled: { label: "Cancelled", tone: "" },
  failed: { label: "Failed", tone: "bad" },
};

/** The public site's sections, each made of several content types (Blogs = blog + article). */
export const TYPE_SECTIONS = [
  { value: "section:blogs", label: "Blogs (blog + article)", types: ["blog", "article"] },
  { value: "section:news", label: "News (news + press release)", types: ["news", "press_release"] },
] as const;

/** A type filter value ("article", or "section:blogs") as the content_type values the API filters on. */
export const typesFor = (value: string): string | string[] =>
  TYPE_SECTIONS.find((s) => s.value === value)?.types.slice() ?? value;

export const CONTENT_TYPES = [
  { value: "blog", label: "Blog" },
  { value: "article", label: "Article" },
  { value: "press_release", label: "Press Release" },
  { value: "news", label: "News" },
  { value: "other", label: "Other" },
] as const;

/** The main path an item takes, for the progress strip on the editor. */
export const FLOW: ItemStatus[] = ["found", "scraped", "checked", "waiting_review", "approved", "published"];

const monthYear = new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric", timeZone: "UTC" });
/** "2026-10-01" → "October 2026" (UTC, so the month never slips across time zones). */
export const fmtMonth = (iso?: string | null) => (iso ? monthYear.format(new Date(iso)) : "—");

export const MAGAZINE_STATUS: Record<MagazineStatus, { label: string; tone: Tone }> = {
  draft: { label: "Draft", tone: "" },
  published: { label: "Published", tone: "ok" },
};

/** The public reader site, for "view on site" links. */
export const READER_URL = (import.meta.env.VITE_READER_URL || "http://localhost:3200").replace(/\/$/, "");

/**
 * Where the browser can load an image. Covers that ship with the Reader are stored as a
 * bare path ("covers/x.webp") and live on the Reader's site, not on this console.
 */
export function imageSrc(url?: string | null): string {
  if (!url) return "";
  if (/^(https?:|data:|blob:)/i.test(url) || url.startsWith("/")) return url;
  return `${READER_URL}/${url}`;
}
